<script lang="ts" setup>
import { computed, inject, onMounted, type Ref, ref } from "vue";
import { provideKeys } from "@magnit/unit-catalog/src/unit-catalog.vars";
import type {
  IProductDetailsEmits,
  IProductDetailsProps,
} from "../types/product-details";
import useProduct from "../composables/useProduct";
import ProductDetailsGallery from "./Gallery.vue";
import ProductDetailsOffer from "./Offer.vue";
import ProductDetailsRating from "./Rating.vue";
import ProductDetailsNutritionFacts from "./NutritionFacts.vue";
import ProductDetailsParameters from "./Parameters.vue";
import ProductDetailsNotice from "./Notice.vue";
import ProductDetailsBadges from "./Badges.vue";
import ProductDetailsPrice from "./Price.vue";
import ProductDetailsInstallAppBanner from "./InstallAppBanner.vue";
import ProductDetailsProgressiveDiscount from "@magnit/unit-product-details/src/components/ProgressiveDiscount.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import type {
  IBasketAction,
  IPromoProduct,
  IPromoProductProgressiveDiscountValue,
} from "@magnit/unit-catalog/src/types";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VStepper from "@magnit/core/src/components/VStepper/VStepper.vue";

const props = withDefaults(defineProps<IProductDetailsProps>(), {
  imageSize: 372,
  showRating: false,
  theme: "base",
  tabbarHeight: "0px",
});

const emit = defineEmits<IProductDetailsEmits>();

const { productId, productName, isAdult, inStock, showNotInStock } = useProduct(
  props.product,
);

const isDialogOpened = inject<Ref<boolean>>(
  provideKeys.isDialogOpened,
  ref(false),
);
const adultApproved = inject<Ref<boolean>>(provideKeys.approved, ref(false));
const blurred = computed(() => isAdult.value && !adultApproved.value);

const withProgressive = computed(() =>
  Boolean(
    "progressiveDiscount" in props.product &&
      props.product.progressiveDiscount?.length,
  ),
);

const promoProduct = computed(
  () => (withProgressive.value && (props.product as IPromoProduct)) || null,
);

const displayedProgressive = ref<IPromoProductProgressiveDiscountValue | null>(
  (withProgressive.value &&
    promoProduct.value &&
    promoProduct.value.progressiveDiscount?.[0] && {
      ...promoProduct.value.progressiveDiscount?.[0],
    }) ||
    null,
);

const isProgressiveDiscountVisible = computed(
  () => withProgressive.value && promoProduct.value,
);

const displayBasketQty = computed(() => {
  if (!props.basketQty) return 0;
  if ("weighted" in props.product) {
    if (props.product.weighted?.step) return props.basketQty / 1000;
  }

  return props.basketQty || 0;
});
const displayBasketUnit = computed(() => {
  if ("weighted" in props.product && props.product.weighted.isWeighted) {
    return "кг";
  }
  return "шт";
});
const basketStep = computed(() => {
  if ("weighted" in props.product) {
    if (props.product.weighted?.isWeighted && props.product.weighted?.step) {
      return props.product.weighted.step;
    }
  }
  return 1;
});

const maxQuanitity = computed(() => {
  if ("quantity" in props.product && props.product.quantity > 0) {
    if ("weighted" in props.product) {
      if (props.product.weighted.isWeighted && props.product.weighted.step) {
        return props.product.quantity / props.product.weighted.step;
      }
    }

    return props.product.quantity;
  }

  return 0;
});

const addToBasketTitle = computed(() =>
  inStock.value ? "Добавить в корзину" : "Нет в наличии",
);

const onBasket = (action: IBasketAction) => {
  emit("basket", props.product.id, basketStep.value, action);
};

onMounted(() => {
  if (isAdult.value && !adultApproved.value) {
    isDialogOpened.value = true;
  }
});
</script>

<template>
  <div
    class="unit-product-details"
    itemscope
    itemtype="http://schema.org/Product"
  >
    <div class="unit-product-details__flexbox">
      <div class="unit-product-details__gallery-container">
        <ProductDetailsGallery
          :key="productId"
          :product="product"
          :blurred="blurred"
          :category-name="props.categoryName"
          @analytics="(...args) => emit('analytics', ...args)"
        />
      </div>
      <div class="unit-product-details__details-container">
        <VHidden
          :hide="{ xs: true, l: false }"
          class="unit-product-details__name"
        >
          <div class="unit-product-details__name-inner">
            <VText font="headline-medium" itemprop="name">
              {{ productName }}
            </VText>

            <VText
              v-if="showNotInStock"
              font="body-large-accent"
              color="accent"
            >
              Нет в наличии
            </VText>
          </div>
        </VHidden>
        <ProductDetailsBadges :product="product" :theme="theme" />
        <ProductDetailsOffer
          v-model:displayed-progressive="displayedProgressive"
          :product="product"
          :category-name="props.categoryName"
          :progressive-discount="displayedProgressive?.label || ''"
          @analytics="(...args) => emit('analytics', ...args)"
        >
          <template #reviews>
            <ProductDetailsRating
              v-if="showRating"
              :product="props.product"
              :category-name="props.categoryName"
              :category-id="props.categoryId"
              :theme="theme"
              @click="$emit('click:rating')"
              @analytics="(...args) => emit('analytics', ...args)"
            />
          </template>

          <template v-if="ecom" #ecom>
            <div class="unit-product-details__ecom">
              <VStepper
                v-if="basketQty && inStock"
                size="m"
                :value="displayBasketQty"
                :max="maxQuanitity"
                :unit="displayBasketUnit"
                :loading="basketLoading"
                @increment="onBasket('inc')"
                @decrement="onBasket('dec')"
                @delete="onBasket('del')"
              />
              <VButton
                v-else
                :disabled="!inStock"
                @click.stop.prevent="onBasket('inc')"
              >
                {{ addToBasketTitle }}
              </VButton>
            </div>
          </template>
        </ProductDetailsOffer>

        <ProductDetailsNutritionFacts :product="product" />
        <VHidden :hide="{ xs: false, l: true }">
          <ProductDetailsProgressiveDiscount
            v-if="isProgressiveDiscountVisible"
            v-model="displayedProgressive"
            :product="props.product"
            :variants="promoProduct?.progressiveDiscount"
            :type="promoProduct?.progressiveDiscountType"
            :category-name="props.categoryName"
            @analytics="(...args) => emit('analytics', ...args)"
          />
        </VHidden>
        <ProductDetailsParameters
          :product="product"
          :category-name="props.categoryName"
          @analytics="(...args) => emit('analytics', ...args)"
        />
        <ProductDetailsNotice />
        <ProductDetailsInstallAppBanner
          v-if="installAppBannerOptions?.appUrl"
          :app-url="installAppBannerOptions.appUrl"
          :images="installAppBannerOptions.images"
          :bg-color="installAppBannerOptions.bgColor"
          :utm-source="installAppBannerOptions.utmSource"
          :product-id="productId"
          class="unit-product-details__banner"
        />
      </div>
    </div>
    <div v-if="ecom" class="unit-product-details__mobile-ecom">
      <VButton v-if="showNotInStock" disabled size="l" full-width>
        Нет в наличии
      </VButton>
      <template v-else>
        <ProductDetailsPrice ecom :product="product" />

        <div class="unit-product-details__mobile-ecom-buttons">
          <VStepper
            v-if="basketQty"
            size="m"
            :value="displayBasketQty"
            :max="maxQuanitity"
            :unit="displayBasketUnit"
            :loading="basketLoading"
            @increment="onBasket('inc')"
            @decrement="onBasket('dec')"
            @delete="onBasket('del')"
          />
          <VButton
            v-else
            size="l"
            full-width
            @click.stop.prevent="onBasket('inc')"
          >
            В корзину
          </VButton>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.unit-product-details {
  &__flexbox {
    display: flex;
    flex-direction: column;
    gap: 0;

    @media (--pl-viewport-from-l) {
      flex-flow: row nowrap;
      gap: var(--pl-unit-x6);
    }
  }

  &__banner {
    margin-top: var(--pl-unit-x5);
  }

  &__gallery-container,
  &__details-container {
    flex: 1;

    @media (--pl-viewport-from-m) {
      width: 100%;
    }

    @media (--pl-viewport-from-l) {
      width: calc(50% - var(--pl-unit-x3));
    }
  }

  &__gallery-container {
    margin-left: calc(-1 * var(--pl-wrapper-indent));
    margin-right: calc(-1 * var(--pl-wrapper-indent));
    margin-bottom: var(--pl-unit-x3);

    @media (--pl-viewport-from-m) {
      margin-left: auto;
      margin-right: auto;
      margin-bottom: var(--pl-unit-x4);
    }

    @media (--pl-viewport-from-l) {
      margin-bottom: auto;
      position: sticky;
      top: var(--unit-product-details-gallery-top-l, 0);
    }
  }

  &__details-container {
    display: flex;
    flex-direction: column;
    padding-bottom: var(--pl-unit-x5);

    @media (--pl-viewport-from-l) {
      max-width: 50%;
      padding-bottom: 0;
    }
  }

  &__name {
    margin-bottom: var(--pl-unit-x4);

    &-inner {
      display: flex;
      flex-direction: column;
      gap: var(--pl-unit-x4);
    }
  }

  &__ecom {
    display: none;
    margin-top: var(--pl-unit-x4);
    width: 190px;

    @media (--pl-viewport-from-l) {
      display: initial;
    }
  }

  &__mobile-ecom {
    display: flex;
    justify-content: space-between;
    position: sticky;
    bottom: v-bind(tabbarHeight);
    background: var(--pl-surface-primary-default);
    padding: var(--pl-unit-x2) var(--pl-wrapper-indent) var(--pl-unit-x3)
      var(--pl-wrapper-indent);
    margin: 0 calc(-1 * var(--pl-wrapper-indent));

    @media (--pl-viewport-from-l) {
      display: none;
    }

    &-buttons {
      min-width: 144px;
    }
  }
}
</style>
